/**
 * The Event Calendar's backend port.
 *
 * The data types are the generated ones from `@amzn/awseceventhub-client` — there
 * is deliberately no second hand-written event shape. This module re-exports the
 * ones the calendar touches so callers have one import surface and never need to
 * know they come from the generated package (same arrangement as IVT's
 * IvtHubClient.ts).
 *
 * The port exists only so the component can be tested against a fake without a
 * Cognito credential chain; the real implementation just forwards to the
 * generated client.
 */

import type {
  Contact,
  EventAttendee,
  CreateEventInput,
  CreateNominationInput,
  Event,
  EventType,
  Nomination,
  SubEvent,
  UpdateEventInput,
  UpdateNominationInput,
} from "@amzn/awseceventhub-client";
import {
  approveNomination,
  createEvent,
  createNomination,
  getCurrentUser,
  getEvent,
  listAllSubEvents,
  getNominationContacts,
  getEventAttendees,
  listAllEvents,
  listNominations,
  rejectNomination,
  updateNominationAttendance,
  updateNomination,
  publishEvent,
  deleteEvent,
  updateEvent,
  uploadEventFile,
  findEventCampaignNode,
  getCampaignNode,
  getCampaignTree,
  listAllCampaignRoots,
  createCampaignNode,
  reparentCampaignNode,
  deleteCampaignNode,
} from "./EventHubClient";
import type { HierarchyApi } from "../components/CampaignHierarchy/hierarchySync";
import { bdBusinessUnitsOf, nominationBusinessUnitsOf } from "../utils/bdProfile";

export type {
  CampaignMembership,
  CampaignNode,
  CampaignTreeNode,
  SubEvent,
  EventAttendee,
  Contact,
  NominationStatus,
  CreateNominationInput,
  Nomination,
  UpdateEventInput,
  UpdateNominationInput,
  BusinessUnitTarget,
  CreateEventInput,
  Event,
  EventContentStatus,
  EventLifecycleStage,
  EventSubType,
  EventTargets,
  EventType,
} from "@amzn/awseceventhub-client";

export interface EventCalendarClient {
  /**
   * `ListEvents` — every event the caller may see.
   *
   * Deliberately NOT a date range: the operation takes only `maxResults` /
   * `nextToken`, so there is no server-side date filter to pass. The
   * implementation pages to exhaustion and the calendar filters to the visible
   * month client-side. Event volume is a few hundred a year, so this is cheaper
   * than a request per month — and it removes the stale-response race the
   * per-month fetch had.
   */
  listEvents(input?: { signal?: AbortSignal }): Promise<Event[]>;

  /**
   * `CreateEvent` — create an event, returning the created resource.
   *
   * The created event is returned rather than discarded because the caller needs
   * the server-generated `eventId` to navigate to what it just created. Events are
   * always created as DRAFT; publishing is a separate `PublishEvent` call.
   */
  createEvent(input: CreateEventInput): Promise<Event>;

  /**
   * `GetEvent` — one event by id.
   *
   * Separate from {@link listEvents} rather than a lookup in its result: the detail page
   * is reachable by URL, so it must work with no list loaded, and it should show the
   * event's current state rather than a snapshot from whenever a list was fetched.
   */
  getEvent(eventId: string, input?: { signal?: AbortSignal }): Promise<Event>;

  /**
   * `ListSubEvents`, every page — the events beneath this one in its campaign hierarchy, at any
   * depth, each with its `parentEventId` and `depth`; only those the caller may see.
   */
  listSubEvents(eventId: string, input?: { signal?: AbortSignal }): Promise<SubEvent[]>;

  /**
   * `UpdateEvent` — write a partial event, returning the updated resource.
   *
   * PATCH semantics: an omitted field is left alone. The service owns `contentStatus`, so
   * this cannot publish. The updated event comes back so a form can re-seed itself from
   * what was actually stored rather than from what it hoped it sent.
   */
  updateEvent(input: UpdateEventInput): Promise<Event>;

  /**
   * `PublishEvent` — move a DRAFT to PUBLISHED, returning the updated event.
   *
   * The service validates first: start and end dates must be present, and a
   * nomination-enabled event also needs both nomination bounds. So this rejects for
   * ordinary reasons — a half-filled event is expected to fail here, and the caller has to
   * surface the message rather than treat it as exceptional.
   */
  publishEvent(eventId: string): Promise<Event>;

  /**
   * `DeleteEvent` — soft-delete the event; afterwards every read treats it as gone. Refused (409)
   * while the event is in a campaign hierarchy: use utils/eventDelete, which takes it out first.
   */
  deleteEvent(eventId: string): Promise<void>;

  /**
   * Uploads a file for the event (`CreateEventFileUpload`, then a PUT to the presigned URL it returns)
   * and resolves to the file id it is referenced by — e.g. an image in the description. Any type, up
   * to 20 MiB. Marketer/admin only, like `updateEvent`.
   */
  uploadEventFile(eventId: string, file: File): Promise<string>;

  /**
   * `ListNominationContacts` — one page of the contacts this caller may nominate.
   *
   * No filter parameter: the service scopes the list to the caller's territories, so the
   * answer already is "the ones you are allowed to nominate". Paginated: keep passing
   * `nextToken` until none comes back (see utils/paging).
   */
  listNominationContacts(input?: {
    signal?: AbortSignal;
    nextToken?: string;
  }): Promise<{ contacts: Contact[]; nextToken?: string }>;

  /**
   * `ListEventAttendees` — one page of an event's attendees (campaign members joined with their
   * nominations), plus every nomination for the event on the first page only. Paginated like
   * listNominationContacts.
   */
  listEventAttendees(input: {
    eventId: string;
    signal?: AbortSignal;
    nextToken?: string;
    /** Also list the campaigns beneath the event. Same value on every page. */
    includeSubCampaigns?: boolean;
  }): Promise<{ attendees: EventAttendee[]; nominations?: Nomination[]; nextToken?: string }>;

  /**
   * `CreateNomination` — nominate a guest to one event.
   *
   * Takes the caller's `clientToken`: a nomination is not idempotent by nature, so without
   * one a double-submitted form puts the same guest on the list twice.
   */
  createNomination(input: CreateNominationInput): Promise<Nomination>;

  /**
   * `ListNominations` — the nominations this caller may see.
   *
   * Unfiltered and unpaged: the service scopes the result to the caller (all of them for a
   * marketer or admin; own plus reports' for anyone else), so every surface takes the whole
   * set and narrows it in memory.
   */
  listNominations(input?: { signal?: AbortSignal }): Promise<Nomination[]>;

  /**
   * `ApproveNomination` / `RejectNomination` — decide a PENDING_REVIEW nomination.
   *
   * Only PENDING_REVIEW can be decided; the opposite decision on an already-decided
   * nomination is a 409. Both email the creating BD, so they are outwardly visible actions.
   * Authorization includes managers of the creator, which the frontend cannot predict — the
   * service's answer is the only one.
   */
  approveNomination(nominationId: string): Promise<Nomination>;
  rejectNomination(nominationId: string, rejectReason?: string): Promise<Nomination>;

  /**
   * `GetCurrentUser` — the roles the service authorizes this caller with. Differs from the id
   * token's groups only while an admin is impersonating; see useEventHubRoles.
   */
  getCurrentUserRoles(input?: { signal?: AbortSignal }): Promise<readonly string[]>;

  /**
   * `GetCurrentUser`'s BD profile for this user (the acted-as user, under impersonation): the
   * phone they last submitted on a nomination — absent for someone who has never nominated — and
   * `businessUnit`, the BU the service resolved for them from the GCR sales hierarchy
   * (`bdBusinessUnit`), which CreateNomination stamps on their nominations — absent when none
   * resolved or the lookup failed. `businessUnits` is every BU their territories fall under (see
   * bdBusinessUnitsOf); when it has several, `businessUnit` is the first. `nominationBusinessUnits`
   * is every BU name they sit under at either hierarchy level, which an event's BUs must include for
   * them to nominate to it (nominationBusinessUnitsOf, utils/nominatableEvents).
   *
   * The saved, self-reported `businessUnit` on the wire is deliberately not passed on: it is no
   * longer what a nomination's BU comes from.
   */
  getCurrentUserProfile(input?: {
    signal?: AbortSignal;
  }): Promise<{
    cellPhone?: string;
    businessUnit?: string;
    businessUnits: readonly string[];
    nominationBusinessUnits: readonly string[];
  }>;

  /**
   * `UpdateNominationAttendance` — the nominating BD confirms (`true`) or withdraws (`false`)
   * that an approved nomination's guest will attend.
   */
  updateNominationAttendance(nominationId: string, confirmed: boolean): Promise<Nomination>;

  /**
   * `UpdateNomination` — the nominating BD edits their nomination; any change sends it back to
   * PENDING_REVIEW and clears the decision and the attendance confirmation. See utils/nominationEdit.
   */
  updateNomination(input: UpdateNominationInput): Promise<Nomination>;

  /** The campaign-hierarchy calls an event's hierarchy load / save makes (see hierarchySync). */
  campaignHierarchy(): HierarchyApi;
}

/**
 * The real implementation, over the generated client.
 *
 * Thin by design: the pagination and the "did CreateEvent actually return the
 * resource" check live in EventHubClient next to the client construction and the
 * credential chain, so this is only the adaptation to the port's shape.
 */
export function eventHubCalendarClient(): EventCalendarClient {
  return {
    listEvents: (input) => listAllEvents(input),
    createEvent: (input) => createEvent(input),
    getEvent: (eventId, input) => getEvent(eventId, input),
    listSubEvents: (eventId, input) => listAllSubEvents(eventId, input),
    updateEvent: (input) => updateEvent(input),
    publishEvent: (eventId) => publishEvent(eventId),
    deleteEvent: (eventId) => deleteEvent(eventId),
    uploadEventFile: (eventId, file) => uploadEventFile(eventId, file),
    listNominationContacts: (input) => getNominationContacts(input),
    listEventAttendees: (input) => getEventAttendees(input),
    createNomination: (input) => createNomination(input),
    listNominations: (input) => listNominations(input),
    approveNomination: (id) => approveNomination(id),
    rejectNomination: (id, reason) => rejectNomination(id, reason),
    getCurrentUserRoles: async (input) => (await getCurrentUser(input)).roles ?? [],
    getCurrentUserProfile: async (input) => {
      const me = await getCurrentUser(input);
      return {
        cellPhone: me.cellPhone,
        businessUnit: me.bdBusinessUnit,
        businessUnits: bdBusinessUnitsOf(me.salesHierarchyL2, me.salesHierarchyL3),
        nominationBusinessUnits: nominationBusinessUnitsOf(
          me.bdBusinessUnit,
          me.salesHierarchyL2,
          me.salesHierarchyL3,
        ),
      };
    },
    campaignHierarchy: () => ({
      findEventNode: (eventId) => findEventCampaignNode(eventId),
      getNode: (nodeId) => getCampaignNode(nodeId),
      getTree: (nodeId) => getCampaignTree(nodeId),
      listRoots: () => listAllCampaignRoots(),
      createEventNode: async (eventId, parentNodeId) =>
        (await createCampaignNode({
          nodeType: "LIFE_CYCLE_EVENT",
          eventId,
          ...(parentNodeId ? { parentNodeId } : {}),
          clientToken: crypto.randomUUID(),
        })).nodeId!,
      createCampaignNode: async (sfdcCampaignId, parentNodeId) =>
        (await createCampaignNode({
          nodeType: "EXTERNAL_CAMPAIGN",
          sfdcCampaignId,
          parentNodeId,
          clientToken: crypto.randomUUID(),
        })).nodeId!,
      reparent: (nodeId, parentNodeId) => reparentCampaignNode(nodeId, parentNodeId),
      remove: (nodeId) => deleteCampaignNode(nodeId),
    }),
    updateNominationAttendance: (id, confirmed) =>
      updateNominationAttendance(id, confirmed ? 'CONFIRMED' : 'NOT_CONFIRMED'),
    updateNomination: (input) => updateNomination(input),
  };
}

/**
 * The calendar's own "status" filter axis. Not a service enum: the backend models
 * `lifecycleStage` with four values, and the filter collapses them to two (see
 * `completionOf`).
 */
export type EventCompletion = "OPEN" | "COMPLETED";

/** Event categories in the order the legend and the type filter list them. */
export const EVENT_TYPE_ORDER: EventType[] = [
  "ONE_P_LOCAL",
  "ONE_P_GLOBAL",
  "THREE_P_LOCAL",
  "THREE_P_GLOBAL",
  "COMPETITOR",
];
